import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, FileText, Upload, Trash2, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { saveMarkScheme, getUserMarkSchemes, deleteMarkScheme } from '../services/database';
import AppNavBar from './AppNavBar';
import { useModal } from '../contexts/ModalContext';

const MySoLPage = () => {
  const [schemes, setSchemes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useModal();

  useEffect(() => {
    loadSchemes();
  }, [currentUser]);

  const loadSchemes = async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const userSchemes = await getUserMarkSchemes(currentUser.uid);
      setSchemes(userSchemes);
    } catch (error) {
      console.error('Error loading schemes:', error);
      await showAlert('Failed to load schemes: ' + error.message, 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleSchemeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !currentUser) return;
    try {
      setUploading(true);
      const schemeName = file.name.replace(/\.[^/.]+$/, "");
      const newScheme = await saveMarkScheme(currentUser.uid, schemeName, file);
      setSchemes([newScheme, ...schemes]);
      event.target.value = '';
      await showAlert('Mark scheme uploaded successfully!', 'Success');
    } catch (error) {
      console.error('Error uploading scheme:', error);
      await showAlert('Failed to upload scheme: ' + error.message, 'Error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteScheme = async (scheme) => {
    const confirmed = await showConfirm(`Are you sure you want to delete "${scheme.name}"?`);
    if (!confirmed) return;
    try {
      await deleteMarkScheme(scheme.id, scheme.filePath);
      setSchemes(schemes.filter(s => s.id !== scheme.id));
      await showAlert('Mark scheme deleted successfully!', 'Success');
    } catch (error) {
      console.error('Error deleting scheme:', error);
      await showAlert('Failed to delete scheme: ' + error.message, 'Error');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    if (date && date.toDate) return date.toDate().toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
        <AppNavBar currentPage="my-sol" />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-400 mx-auto mb-4"></div>
            <p className="text-ink-900 text-lg">Loading your mark schemes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="my-sol" />

      <div className="py-8">
        <div className="max-w-5xl mx-auto px-4">

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-coral-500 leading-tight mb-4 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
              📋 My Mark Schemes
            </h1>
            <p className="text-lg text-ink-700">{schemes.length} mark scheme{schemes.length !== 1 ? 's' : ''} uploaded</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <label className="bg-coral-300 text-white px-6 py-3 rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold shadow-lg cursor-pointer flex items-center">
              {uploading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  Upload Mark Scheme
                </>
              )}
              <input
                type="file"
                onChange={handleSchemeUpload}
                accept=".pdf,.doc,.docx,.txt"
                disabled={uploading}
                className="hidden"
              />
            </label>
            <button
              onClick={() => navigate('/mark-work')}
              className="bg-coral-300 text-white px-6 py-3 rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold shadow-lg flex items-center"
            >
              <FileText className="w-5 h-5 mr-2" />
              Go to Mark Work
            </button>
          </div>

          <p className="text-center text-sm text-ink-500 mb-8">
            Upload your Schemes of Learning and Mark Schemes — PDF format recommended for best results. DOCX files may not extract accurately.
          </p>

          {/* Schemes List */}
          <div className="bg-white rounded-lg border-4 border-ink-900 shadow-xl p-6">
            <div className="flex items-center mb-6">
              <BookOpen className="w-6 h-6 text-coral-500 mr-3" />
              <h2 className="text-xl font-semibold text-ink-800">Your Schemes</h2>
              <span className="ml-auto bg-coral-100 text-coral-800 text-sm px-2 py-1 rounded-full">
                {schemes.length}
              </span>
            </div>

            {schemes.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto h-20 w-20 text-sand-300 mb-4">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-ink-800 mb-2">No mark schemes yet</h3>
                <p className="text-ink-600 text-sm mb-4">Upload your first mark scheme above to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {schemes.map((scheme) => (
                  <div key={scheme.id} className="border-4 border-ink-900 rounded-lg p-4 hover:shadow-md transition-shadow bg-sand-50">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex items-start flex-1 min-w-0">
                        <FileText className="w-5 h-5 mr-3 text-coral-600 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <span className="font-semibold text-ink-900 block truncate">{scheme.name}</span>
                          <span className="text-xs text-ink-500 block">
                            Uploaded: {formatDate(scheme.uploadDate)} • {formatFileSize(scheme.fileSize)}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2 flex-shrink-0">
                        
                        <a
                          href={scheme.fileURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-coral-300 text-white hover:bg-coral-500 px-3 py-2 rounded text-sm transition duration-200 font-medium"
                        >
                          View
                        </a>
                        <button
                          onClick={() => handleDeleteScheme(scheme)}
                          className="bg-coral-300 text-white hover:bg-coral-500 px-3 py-2 rounded text-sm transition duration-200 flex items-center font-medium"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MySoLPage;